/**
 * @fileoverview Consent Manager Service
 * Singleton service for managing GDPR-compliant cookie consent preferences
 * and Google Consent Mode v2 integration
 */

import { CONSENT_VERSION, EXPIRY_MONTHS, STORAGE_KEY } from '../../types/consent.types';

/**
 * ConsentManager - Singleton service for consent management
 */
class ConsentManager {
  constructor() {
    if (ConsentManager.instance) {
      return ConsentManager.instance;
    }
    
    this.listeners = [];
    this.initialized = false;
    ConsentManager.instance = this;
  }

  /**
   * Initialize the consent manager
   * Sets default consent mode before any tags load
   */
  initialize() {
    if (this.initialized) return;
    
    this.setDefaultConsentMode();
    
    // Load existing consent and update consent mode
    const existingConsent = this.loadFromStorage();
    if (existingConsent) {
      this.updateConsentMode(existingConsent);
    }
    
    this.initialized = true;
  }

  /**
   * Get current consent preferences
   * @returns {ConsentPreferences|null}
   */
  getConsent() {
    return this.loadFromStorage();
  }

  /**
   * Set consent preferences
   * @param {ConsentPreferences} preferences
   */
  setConsent(preferences) {
    const consentData = {
      essential: true, // Always true
      analytics: preferences.analytics,
      advertising: preferences.advertising,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
      expiresAt: Date.now() + (EXPIRY_MONTHS * 30 * 24 * 60 * 60 * 1000)
    };
    
    this.saveToStorage(consentData);
    this.updateConsentMode(consentData);
    this.notifyListeners(consentData);
  }

  /**
   * Check if user has provided consent
   * @returns {boolean}
   */
  hasConsent() {
    const consent = this.loadFromStorage();
    return consent !== null;
  }

  /**
   * Check if analytics consent is granted
   * @returns {boolean}
   */
  hasAnalyticsConsent() {
    const consent = this.loadFromStorage();
    return consent ? consent.analytics : false;
  }

  /**
   * Check if advertising consent is granted
   * @returns {boolean}
   */
  hasAdvertisingConsent() {
    const consent = this.loadFromStorage();
    return consent ? consent.advertising : false;
  }

  /**
   * Set default consent mode (denied for GDPR compliance)
   */
  setDefaultConsentMode() {
    if (typeof window === 'undefined' || !window.gtag) return;
    
    window.gtag('consent', 'default', {
      'ad_storage': 'denied',
      'analytics_storage': 'granted',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'functionality_storage': 'granted',
      'security_storage': 'granted',
      'wait_for_update': 500
    });
  }

  /**
   * Update consent mode based on user preferences
   * @param {ConsentPreferences} preferences
   */
  updateConsentMode(preferences) {
    if (typeof window === 'undefined' || !window.gtag) return;
    
    window.gtag('consent', 'update', {
      'ad_storage': preferences.advertising ? 'granted' : 'denied',
      'analytics_storage': preferences.analytics ? 'granted' : 'denied',
      'ad_user_data': preferences.advertising ? 'granted' : 'denied',
      'ad_personalization': preferences.advertising ? 'granted' : 'denied'
    });
  }

  /**
   * Save consent preferences to localStorage
   * @param {ConsentPreferences} preferences
   */
  saveToStorage(preferences) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Failed to save consent preferences:', error);
    }
  }

  /**
   * Load consent preferences from localStorage
   * @returns {ConsentPreferences|null}
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      
      const data = JSON.parse(stored);
      
      // Check expiry
      if (data.expiresAt < Date.now()) {
        this.clearStorage();
        return null;
      }
      
      // Check version compatibility
      if (data.version !== CONSENT_VERSION) {
        this.clearStorage();
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Failed to load consent preferences:', error);
      return null;
    }
  }

  /**
   * Clear consent preferences from storage
   */
  clearStorage() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear consent preferences:', error);
    }
  }

  /**
   * Register a callback for consent changes
   * @param {Function} callback
   * @returns {Function} Unsubscribe function
   */
  onConsentChange(callback) {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Notify all listeners of consent changes
   * @param {ConsentPreferences} preferences
   */
  notifyListeners(preferences) {
    this.listeners.forEach(listener => {
      try {
        listener(preferences);
      } catch (error) {
        console.error('Error in consent change listener:', error);
      }
    });
  }
}

// Export singleton instance
const consentManager = new ConsentManager();
export default consentManager;
